import emailjs from '@emailjs/browser';
import { Booking, Camp } from '../types';
import { generateBookingPDF } from './pdfGenerator';
import { getPublicSiteOrigin } from './siteUrl';

// EmailJS Configuration
// You'll need to set these up in your .env file or replace with your actual values
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'your_service_id';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'your_template_id';
const EMAILJS_CONFIRMATION_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CONFIRMATION_TEMPLATE_ID || 'your_confirmation_template_id';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key';

/** Shown as the sender address when EmailJS templates use {{from_email}} (see EMAILJS_SETUP.md). */
const SITE_FROM_EMAIL =
  process.env.REACT_APP_SITE_FROM_EMAIL || 'info@southerncomfortcamps.com';

// Initialize EmailJS
if (EMAILJS_PUBLIC_KEY !== 'your_public_key') {
  emailjs.init(EMAILJS_PUBLIC_KEY);
}

export interface EmailData {
  bookingId: string;
  campName: string;
  location: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  guests: number;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  specialRequests?: string;
  totalPrice: number;
  pricePerNight: number;
}

export const sendBookingEmail = async (
  booking: Booking,
  camp: Camp
): Promise<{ success: boolean; error?: string; emailSent?: boolean }> => {
  try {
    const nights = Math.ceil(
      (booking.checkOut.getTime() - booking.checkIn.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Generate PDF
    const pdfDoc = generateBookingPDF(booking, camp);

    // Prepare email data
    const emailData: EmailData = {
      bookingId: booking.id,
      campName: camp.name,
      location: camp.location,
      checkIn: booking.checkIn.toLocaleDateString(),
      checkOut: booking.checkOut.toLocaleDateString(),
      nights,
      guests: booking.guests,
      guestName: booking.guestName,
      guestEmail: booking.guestEmail,
      guestPhone: booking.guestPhone,
      specialRequests: booking.specialRequests,
      totalPrice: booking.totalPrice,
      pricePerNight: camp.pricePerNight,
    };

    // Generate approval link
    const approvalData = encodeURIComponent(JSON.stringify({
      bookingId: booking.id,
      guestEmail: booking.guestEmail,
      guestName: booking.guestName,
      guestPhone: booking.guestPhone,
      campId: camp.id,
      campName: camp.name,
      checkIn: booking.checkIn.toISOString(),
      checkOut: booking.checkOut.toISOString(),
      guests: booking.guests,
      totalPrice: booking.totalPrice,
    }));
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const approvalLink = `${baseUrl}/approve-booking?data=${approvalData}`;

    // Prepare template parameters
    const templateParams = {
      to_email: 'info@southerncomfortcamps.com',
      from_name: 'Southern Comfort Camps Booking System',
      from_email: SITE_FROM_EMAIL,
      reply_to: emailData.guestEmail,
      subject: `New Booking Request - ${camp.name} - ${booking.guestName}`,
      
      // Booking details
      booking_id: emailData.bookingId,
      camp_name: emailData.campName,
      location: emailData.location,
      check_in: emailData.checkIn,
      check_out: emailData.checkOut,
      nights: emailData.nights.toString(),
      guests: emailData.guests.toString(),
      
      // Guest details
      guest_name: emailData.guestName,
      guest_email: emailData.guestEmail,
      guest_phone: emailData.guestPhone,
      special_requests: emailData.specialRequests || 'None',
      
      // Pricing
      price_per_night: `$${emailData.pricePerNight.toFixed(2)}`,
      total_price: `$${emailData.totalPrice.toFixed(2)}`,
      
      // Approval link
      approval_link: approvalLink,
      
      // PDF attachment (Note: EmailJS free tier doesn't support attachments directly)
      // You may need to use a different service or send PDF link
      message: `
        <h2>New Booking Request</h2>
        
        <p><strong>Booking ID:</strong> ${emailData.bookingId}</p>
        
        <h3>Camp Details</h3>
        <p><strong>Camp:</strong> ${emailData.campName}<br>
        <strong>Location:</strong> ${emailData.location}</p>
        
        <h3>Dates</h3>
        <p><strong>Check-in:</strong> ${emailData.checkIn}<br>
        <strong>Check-out:</strong> ${emailData.checkOut}<br>
        <strong>Nights:</strong> ${emailData.nights}<br>
        <strong>Guests:</strong> ${emailData.guests}</p>
        
        <h3>Guest Information</h3>
        <p><strong>Name:</strong> ${emailData.guestName}<br>
        <strong>Email:</strong> ${emailData.guestEmail}<br>
        <strong>Phone:</strong> ${emailData.guestPhone}</p>
        
        <p><strong>Special Requests:</strong> ${emailData.specialRequests || 'None'}</p>
        
        <h3>Pricing</h3>
        <p>$${emailData.pricePerNight.toFixed(2)} × ${emailData.nights} nights = <strong>$${emailData.totalPrice.toFixed(2)}</strong></p>
        
        <hr style="margin: 20px 0;">
        
        <p><strong>To approve this booking and send a confirmation email to the guest, click the link below:</strong></p>
        <p><a href="${approvalLink}" style="background-color: #1a5f7a; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; display: inline-block;">Approve Booking</a></p>
        
        <p style="font-size: 12px; color: #666;">Or copy and paste this link into your browser:<br>
        ${approvalLink}</p>
        
        <hr style="margin: 20px 0;">
        
        <p>Please review this booking request.</p>
      `,
    };

    // Send email via EmailJS
    if (
      EMAILJS_SERVICE_ID === 'your_service_id' ||
      EMAILJS_TEMPLATE_ID === 'your_template_id' ||
      EMAILJS_PUBLIC_KEY === 'your_public_key'
    ) {
      console.warn('EmailJS not configured. Booking data:', templateParams);
      pdfDoc.save(`booking-${booking.id}.pdf`);
      return {
        success: true,
        emailSent: false,
        error:
          'Email was not sent: EmailJS environment variables are missing in this build. A PDF was downloaded instead — add REACT_APP_EMAILJS_* to your hosting (e.g. Cloudflare Pages) and redeploy.',
      };
    }

    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );

    // Also save PDF for download/attachment
    pdfDoc.save(`booking-${booking.id}.pdf`);

    return { success: true, emailSent: true };
  } catch (error) {
    console.error('Error sending email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send email',
    };
  }
};

// Send a simple enquiry email to the team for manual follow-up (no payment/approval link, no PDF).
export interface EnquiryPayload {
  campName: string;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  guests: number;
  preferredCheckIn?: string;
  preferredCheckOut?: string;
  nights?: number;
  message?: string;
}

export const sendEnquiryEmail = async (
  enquiry: EnquiryPayload
): Promise<{ success: boolean; error?: string; emailSent?: boolean }> => {
  try {
    const reviewUrl = `${getPublicSiteOrigin()}/manage-bookings`;
    const nightsText = enquiry.nights
      ? `${enquiry.nights} night${enquiry.nights === 1 ? '' : 's'}`
      : 'Not specified';

    const messageHtml = `
        <h2>New Enquiry</h2>

        <h3>Camp</h3>
        <p><strong>Camp of interest:</strong> ${enquiry.campName}</p>

        <h3>Preferred Dates</h3>
        <p><strong>Preferred check-in:</strong> ${enquiry.preferredCheckIn || 'Flexible / not specified'}<br>
        <strong>Preferred check-out:</strong> ${enquiry.preferredCheckOut || 'Flexible / not specified'}<br>
        <strong>Nights:</strong> ${nightsText}<br>
        <strong>Guests:</strong> ${enquiry.guests}</p>

        <h3>Contact</h3>
        <p><strong>Name:</strong> ${enquiry.guestName}<br>
        <strong>Email:</strong> ${enquiry.guestEmail}<br>
        <strong>Phone:</strong> ${enquiry.guestPhone}</p>

        <h3>Message</h3>
        <p>${enquiry.message ? enquiry.message.replace(/\n/g, '<br>') : 'No message provided.'}</p>

        <h3>Review &amp; confirm</h3>
        <p><a href="${reviewUrl}" style="background-color:#1a5f7a;color:#fff;padding:12px 24px;text-decoration:none;border-radius:4px;display:inline-block;">Open Manage Bookings</a></p>
        <p style="font-size:12px;color:#666;">Or paste this link into your browser:<br>${reviewUrl}</p>

        <hr style="margin: 20px 0;">
        <p>Reply directly to this email to respond to ${enquiry.guestName}.</p>
      `;

    const templateParams = {
      to_email: 'info@southerncomfortcamps.com',
      from_name: 'Southern Comfort Camps Website',
      from_email: SITE_FROM_EMAIL,
      reply_to: enquiry.guestEmail,
      subject: `New Enquiry - ${enquiry.campName} - ${enquiry.guestName}`,

      // Flat fields in case the template references them directly
      camp_name: enquiry.campName,
      guest_name: enquiry.guestName,
      guest_email: enquiry.guestEmail,
      guest_phone: enquiry.guestPhone,
      guests: enquiry.guests.toString(),
      nights: nightsText,
      check_in: enquiry.preferredCheckIn || 'Flexible / not specified',
      check_out: enquiry.preferredCheckOut || 'Flexible / not specified',
      special_requests: enquiry.message || 'None',
      // Short, clickable link to review/confirm (replaces the old long approve-data link)
      review_url: reviewUrl,
      manage_url: reviewUrl,
      approval_link: reviewUrl,

      message: messageHtml,
      body: messageHtml,
    };

    if (
      EMAILJS_SERVICE_ID === 'your_service_id' ||
      EMAILJS_TEMPLATE_ID === 'your_template_id' ||
      EMAILJS_PUBLIC_KEY === 'your_public_key'
    ) {
      console.warn('EmailJS not configured. Enquiry data:', templateParams);
      return {
        success: true,
        emailSent: false,
        error:
          'Email was not sent: EmailJS environment variables are missing in this build. Add REACT_APP_EMAILJS_* to your hosting (e.g. Cloudflare Pages) and redeploy.',
      };
    }

    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );

    return { success: true, emailSent: true };
  } catch (error) {
    console.error('Error sending enquiry email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send enquiry',
    };
  }
};

// Send confirmation email to guest when booking is approved (includes Stripe payment link)
export interface ConfirmationEmailPayload {
  bookingId: string;
  guestEmail: string;
  guestName: string;
  campName: string;
  checkIn: Date;
  checkOut: Date;
  guests: number;
  totalPrice: number;
  paymentPageUrl: string;
  confirmationPdfPageUrl: string;
}

export const sendConfirmationEmail = async ({
  bookingId,
  guestEmail,
  guestName,
  campName,
  checkIn,
  checkOut,
  guests,
  totalPrice,
  paymentPageUrl,
  confirmationPdfPageUrl,
}: ConfirmationEmailPayload): Promise<{ success: boolean; error?: string }> => {
  try {
    const nights = Math.ceil(
      (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)
    );

    const paymentLinesHtml = `
        <h3>Secure payment</h3>
        <p>Please complete payment online using our Stripe Checkout page:</p>
        <p><a href="${paymentPageUrl}" style="background-color:#1a5f7a;color:#fff;padding:12px 24px;text-decoration:none;border-radius:4px;display:inline-block;">Pay securely online</a></p>
        <p style="font-size:12px;color:#666;">Or copy this link:<br>${paymentPageUrl}</p>
        <h3>Confirmation PDF</h3>
        <p>Download your confirmation (includes the payment link):</p>
        <p><a href="${confirmationPdfPageUrl}">${confirmationPdfPageUrl}</a></p>`;

    const messageHtml = `
        <h2>Booking Confirmed!</h2>
        
        <p>Dear ${guestName},</p>
        
        <p>Your booking has been confirmed! We look forward to hosting you at Southern Comfort Camps.</p>
        
        <h3>Booking Details</h3>
        <p><strong>Booking ID:</strong> ${bookingId}<br>
        <strong>Camp:</strong> ${campName}</p>
        
        <h3>Dates</h3>
        <p><strong>Check-in:</strong> ${checkIn.toLocaleDateString()}<br>
        <strong>Check-out:</strong> ${checkOut.toLocaleDateString()}<br>
        <strong>Nights:</strong> ${nights}<br>
        <strong>Guests:</strong> ${guests}</p>
        
        <p><strong>Total:</strong> $${totalPrice.toFixed(2)} AUD</p>
        ${paymentLinesHtml}
        <hr style="margin: 20px 0;">
        
        <p>If you have any questions, please contact us at <a href="mailto:info@southerncomfortcamps.com">info@southerncomfortcamps.com</a></p>
        
        <p>Best regards,<br>
        Southern Comfort Camps Team</p>
      `;

    const templateParams = {
      to_email: guestEmail,
      from_name: 'Southern Comfort Camps',
      from_email: SITE_FROM_EMAIL,
      reply_to: SITE_FROM_EMAIL,
      subject: `Booking Confirmed - ${campName}`,

      guest_name: guestName,
      booking_id: bookingId,
      camp_name: campName,
      check_in: checkIn.toLocaleDateString(),
      check_out: checkOut.toLocaleDateString(),
      nights: nights.toString(),
      guests: guests.toString(),
      // Legacy confirmation templates often omit {{payment_page_url}}; URLs are duplicated here so Total always shows them (plain text, auto-linked by most mail clients).
      total_price: `$${totalPrice.toFixed(2)} AUD · Pay online: ${paymentPageUrl} · Confirmation PDF: ${confirmationPdfPageUrl}`,
      payment_page_url: paymentPageUrl,
      confirmation_pdf_url: confirmationPdfPageUrl,

      message: messageHtml,
      body: messageHtml,
      payment_section_html: paymentLinesHtml.trim(),
    };

    if (EMAILJS_SERVICE_ID === 'your_service_id' || EMAILJS_CONFIRMATION_TEMPLATE_ID === 'your_confirmation_template_id') {
      console.warn('EmailJS confirmation template not configured.');
      return {
        success: false,
        error: 'EmailJS confirmation template not configured.',
      };
    }

    await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_CONFIRMATION_TEMPLATE_ID,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );

    return { success: true };
  } catch (error) {
    console.error('Error sending confirmation email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send confirmation email',
    };
  }
};

